import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  TrendingUp,
  Eye,
  BarChart3,
  Home,
  MapPin,
  Calendar,
  Star,
  Users,
  ExternalLink,
  Monitor,
  DollarSign,
  Zap,
  Clock,
  Bookmark,
  Heart,
  Bed,
  Bath,
  ChevronDown,
} from "lucide-react";

interface SpotlightIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EXAMPLE_URL = "/s/caldwell-group";

/* ------------------------------------------------------------------ */
/*  Mini-preview components — styled to match actual Spotlight sections */
/* ------------------------------------------------------------------ */

const ACCENT = "#3B82F6";

/** Matches SpotlightHero: full-bleed image, gradient overlay, big headline, accent bar, team name */
function HeroPreview() {
  return (
    <div className="relative rounded-lg overflow-hidden" style={{ aspectRatio: "16/9" }}>
      {/* Background image */}
      <img
        src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Gradient overlay — matches real component */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.2) 60%, transparent 100%)",
        }}
      />
      {/* Content at bottom — matches real layout */}
      <div className="absolute inset-x-0 bottom-0 p-3">
        <p className="text-sm md:text-base font-bold text-white leading-tight">
          587 Homes Sold &amp; $285M in Career Sales
        </p>
        <div
          className="w-8 h-0.5 rounded-full my-1.5"
          style={{ backgroundColor: ACCENT }}
        />
        <p className="text-[9px] text-white/80 leading-relaxed">
          Outpacing the market with 38% fewer days to close
        </p>
        <p className="text-[8px] text-white uppercase tracking-[0.15em] font-medium mt-1.5">
          The Caldwell Group
        </p>
      </div>
      {/* Animated scroll indicator */}
      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 opacity-50">
        <ChevronDown className="h-3 w-3 text-white" />
      </div>
    </div>
  );
}

/** Matches SpotlightSpeedToSold: light bg, glass cards, icon circles, big numbers */
function TrackRecordPreview() {
  const stats = [
    { icon: Home, label: "Total Homes Sold", value: "587" },
    { icon: DollarSign, label: "Avg Sale Price", value: "$485K" },
    { icon: TrendingUp, label: "Price Range", value: "$250K–$950K" },
  ];
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {stats.map((s) => (
        <div
          key={s.label}
          className="rounded-xl border border-slate-200 bg-white/60 p-2 text-center shadow-sm"
        >
          <div
            className="inline-flex items-center justify-center w-6 h-6 rounded-full mb-1"
            style={{ backgroundColor: `${ACCENT}15` }}
          >
            <s.icon className="h-3 w-3" style={{ color: ACCENT }} />
          </div>
          <p className="text-sm font-bold text-slate-900 tabular-nums leading-tight">
            {s.value}
          </p>
          <p className="text-[8px] text-slate-400 mt-0.5">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

/** Matches SpotlightDigitalFootprint: color dots, thin bars, icon stat circles */
function DigitalFootprintPreview() {
  const platforms = [
    { name: "Zillow", color: "#006AFF", pct: 88, value: "4.8K" },
    { name: "Realtor.com", color: "#D92228", pct: 64, value: "3.4K" },
    { name: "Facebook", color: "#1877F2", pct: 45, value: "2.1K" },
    { name: "Instagram", color: "#E4405F", pct: 38, value: "1.6K" },
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white/60 p-3 shadow-sm space-y-2.5">
      {platforms.map((p) => (
        <div key={p.name}>
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: p.color }}
            />
            <span className="text-[11px] text-slate-700 flex-1">{p.name}</span>
            <span className="text-[11px] font-semibold text-slate-900 tabular-nums">
              {p.value}
            </span>
          </div>
          <div className="mt-1 ml-4 h-1 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${p.pct}%`,
                backgroundColor: p.color,
                opacity: 0.7,
              }}
            />
          </div>
        </div>
      ))}
      {/* Stats row matching real component */}
      <div className="grid grid-cols-3 gap-2 border-t border-slate-100 pt-2.5">
        {[
          { icon: Eye, value: "8,234", label: "Views" },
          { icon: Bookmark, value: "412", label: "Saves" },
          { icon: Heart, value: "5.0%", label: "Save Rate" },
        ].map((s) => (
          <div key={s.label} className="text-center">
            <div
              className="inline-flex items-center justify-center w-5 h-5 rounded-full mb-0.5"
              style={{ backgroundColor: `${ACCENT}15` }}
            >
              <s.icon className="h-2.5 w-2.5" style={{ color: ACCENT }} />
            </div>
            <p className="text-xs font-bold text-slate-900">{s.value}</p>
            <p className="text-[8px] text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Matches SpotlightMarketComparison: dark navy bg, glass card, Zap icon, demand gauge */
function MarketComparisonPreview() {
  const circumference = 2 * Math.PI * 15.5;
  const strokeDash = 0.75 * circumference;
  return (
    <div
      className="rounded-xl p-3 space-y-3"
      style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)" }}
    >
      {/* DOM bars */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-2.5 space-y-2.5">
        <div className="flex items-center gap-1.5 mb-1">
          <Clock className="h-2.5 w-2.5 text-white/40" />
          <span className="text-[9px] text-white/60 uppercase tracking-wider">
            Days on Market
          </span>
        </div>
        {/* Team bar */}
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <div className="flex items-center gap-1">
              <Zap className="h-2.5 w-2.5" style={{ color: ACCENT }} />
              <span className="text-[10px] font-medium text-white">Our Team</span>
            </div>
            <span className="text-sm font-bold text-white">28 days</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: "62%", backgroundColor: ACCENT }}
            />
          </div>
        </div>
        {/* Market bar */}
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10px] font-medium text-white/50">Market Average</span>
            <span className="text-sm font-bold text-white/40">45 days</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-white/20" style={{ width: "100%" }} />
          </div>
        </div>
      </div>

      {/* Demand gauge */}
      <div className="flex items-center gap-3">
        <svg viewBox="0 0 36 36" className="w-11 h-11 shrink-0 -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
          <circle
            cx="18"
            cy="18"
            r="15.5"
            fill="none"
            stroke="#22c55e"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${strokeDash} ${circumference}`}
          />
        </svg>
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-white">7.5</span>
            <span className="text-[9px] text-white/30">/10</span>
          </div>
          <span className="text-[9px] font-semibold text-emerald-400">High Demand</span>
        </div>
      </div>
    </div>
  );
}

/** Matches SpotlightReviews: dark navy bg, big white rating, gold stars, AI summary */
function ReviewsPreview() {
  return (
    <div
      className="rounded-xl p-3 space-y-2.5"
      style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)" }}
    >
      {/* Big rating */}
      <div className="flex items-end gap-2">
        <span className="text-3xl font-bold text-white leading-none">4.8</span>
        <div className="pb-0.5">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                className="h-3.5 w-3.5"
                fill={i <= 5 ? "#FBBF24" : "transparent"}
                stroke={i <= 5 ? "#FBBF24" : "rgba(255,255,255,0.2)"}
                strokeWidth={1.5}
              />
            ))}
          </div>
          <p className="text-[9px] text-white/40 mt-0.5">47 reviews on Zillow</p>
        </div>
      </div>
      {/* AI summary */}
      <div>
        <p className="text-[10px] text-white/60 leading-relaxed">
          "Clients praise the team's expertise, responsiveness, and ability to
          close deals quickly in competitive markets..."
        </p>
        <p className="text-[9px] text-amber-400/70 mt-1 flex items-center gap-1">
          <Sparkles className="h-2.5 w-2.5" /> AI-generated summary
        </p>
      </div>
      {/* Sample review */}
      <div className="border-t border-white/10 pt-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold text-white">Sarah M.</span>
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="h-2.5 w-2.5" fill="#FBBF24" stroke="#FBBF24" strokeWidth={1.5} />
            ))}
          </div>
        </div>
        <p className="text-[9px] text-white/50 mt-1 leading-relaxed line-clamp-2">
          They made the entire process seamless. Our home sold in just 5 days above asking price.
        </p>
      </div>
    </div>
  );
}

/** Matches SpotlightShowings: rounded-full circles in a grid, stats with icon circles */
function ShowingsPreview() {
  const weeks = [
    [0, 2, 0, 3, 1, 0, 4],
    [1, 0, 2, 0, 3, 2, 5],
    [0, 1, 0, 4, 2, 1, 3],
    [2, 0, 3, 1, 0, 2, 6],
  ];
  return (
    <div className="space-y-2.5">
      {/* Day headers */}
      <div className="flex gap-1 justify-center">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="w-5 text-center text-[8px] text-slate-400">
            {d}
          </div>
        ))}
      </div>
      {/* Calendar grid — rounded-full circles like real component */}
      <div className="space-y-1">
        {weeks.map((week, wi) => (
          <div key={wi} className="flex gap-1 justify-center">
            {week.map((val, di) => (
              <div
                key={`${wi}-${di}`}
                className="w-5 h-5 rounded-full"
                style={{
                  backgroundColor: ACCENT,
                  opacity: val === 0 ? 0.06 : Math.min(0.2 + val * 0.2, 1),
                }}
              />
            ))}
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="flex items-center justify-center gap-1 text-[8px] text-slate-400">
        <span>Less</span>
        {[0.06, 0.2, 0.4, 0.6, 1].map((o, i) => (
          <div
            key={i}
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: ACCENT, opacity: o }}
          />
        ))}
        <span>More</span>
      </div>
      {/* Stats row */}
      <div className="grid grid-cols-3 gap-1.5 pt-1">
        {[
          { value: "28", label: "Showings" },
          { value: "5", label: "Open Houses" },
          { value: "142", label: "Visitors" },
        ].map((s) => (
          <div key={s.label} className="text-center">
            <p className="text-sm font-bold text-slate-900">{s.value}</p>
            <p className="text-[8px] text-slate-400 uppercase tracking-wider">
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Matches SpotlightHomesCarousel: 3:4 cards with photo, gradient overlay, white text, view badge */
function ListingsPreview() {
  const homes = [
    { price: "$1.2M", addr: "4521 Lakeview Dr", city: "Westlake", beds: 5, baths: 4, views: 342, img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=300&q=80" },
    { price: "$875K", addr: "102 Oak Hollow Ln", city: "Austin", beds: 4, baths: 3, views: 218, img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=300&q=80" },
    { price: "$640K", addr: "8830 Sunrise Ct", city: "Cedar Park", beds: 3, baths: 2, views: 156, img: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=300&q=80" },
  ];
  return (
    <div className="flex gap-1.5 overflow-hidden">
      {homes.map((h) => (
        <div
          key={h.addr}
          className="w-[32%] shrink-0 rounded-lg overflow-hidden relative"
          style={{ aspectRatio: "3/4" }}
        >
          {/* Listing photo */}
          <img
            src={h.img}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Gradient overlay — matches real component */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          {/* Content at bottom */}
          <div className="absolute inset-x-0 bottom-0 p-1.5">
            <p className="text-[11px] font-bold text-white">{h.price}</p>
            <p className="text-[7px] text-white font-medium truncate">{h.addr}</p>
            <p className="text-[7px] text-white/60">{h.city}</p>
            <div className="flex items-center gap-1.5 mt-0.5 text-[7px] text-white/70">
              <span className="flex items-center gap-0.5">
                <Bed className="h-2 w-2" /> {h.beds}
              </span>
              <span className="flex items-center gap-0.5">
                <Bath className="h-2 w-2" /> {h.baths}
              </span>
            </div>
          </div>
          {/* Views badge */}
          <div className="absolute top-1 right-1 flex items-center gap-0.5 bg-black/50 backdrop-blur-sm rounded-full px-1.5 py-0.5">
            <Eye className="h-2 w-2 text-white/80" />
            <span className="text-[7px] font-semibold text-white">{h.views}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Matches SpotlightMap: dark map bg, colored markers with white border + glow, glass label */
function MapPreview() {
  // Dummy pin positions scattered across the preview (percentages)
  const pins = [
    { x: 22, y: 30 },
    { x: 35, y: 55 },
    { x: 48, y: 25 },
    { x: 55, y: 65 },
    { x: 62, y: 40 },
    { x: 70, y: 58 },
    { x: 78, y: 32 },
    { x: 30, y: 72 },
    { x: 42, y: 48 },
    { x: 85, y: 50 },
  ];
  return (
    <div className="relative rounded-lg overflow-hidden h-32" style={{ background: "#1a1a2e" }}>
      {/* Faint grid lines to suggest a map */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.06]">
        {[20, 40, 60, 80].map((y) => (
          <line key={`h${y}`} x1="0" y1={`${y}%`} x2="100%" y2={`${y}%`} stroke="white" strokeWidth="1" />
        ))}
        {[20, 40, 60, 80].map((x) => (
          <line key={`v${x}`} x1={`${x}%`} y1="0" x2={`${x}%`} y2="100%" stroke="white" strokeWidth="1" />
        ))}
      </svg>
      {/* Markers — colored circles with white border and glow, matching real SpotlightMap */}
      {pins.map((pin, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: `${pin.x}%`,
            top: `${pin.y}%`,
            transform: "translate(-50%, -50%)",
          }}
        >
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{
              backgroundColor: ACCENT,
              border: "1.5px solid white",
              boxShadow: `0 0 8px ${ACCENT}80, 0 1px 3px rgba(0,0,0,0.3)`,
            }}
          />
        </div>
      ))}
      {/* Glass label — matches real component */}
      <div className="absolute top-2 left-2 backdrop-blur-md bg-white/10 border border-white/20 rounded-md px-2 py-1">
        <p className="text-[8px] text-white font-medium">Our Coverage Area</p>
      </div>
    </div>
  );
}

/** Matches SpotlightTeam: circular headshots with name below, hover lift */
function TeamPreview() {
  const agents = [
    { name: "Sarah C.", img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&q=80" },
    { name: "James R.", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&q=80" },
    { name: "Maria L.", img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&q=80" },
    { name: "David K.", img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=120&q=80" },
  ];
  return (
    <div className="flex justify-center gap-4">
      {agents.map((a) => (
        <div key={a.name} className="text-center">
          <img
            src={a.img}
            alt=""
            className="w-12 h-12 rounded-full object-cover shadow-md mx-auto"
          />
          <p className="text-[9px] font-semibold text-slate-800 mt-1">{a.name}</p>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Feature card definitions                                           */
/* ------------------------------------------------------------------ */

const features: {
  icon: typeof Sparkles;
  title: string;
  description: string;
  preview?: () => React.ReactNode;
}[] = [
  {
    icon: Sparkles,
    title: "Cinematic Hero",
    description:
      "AI-generated headlines with your listing photos or custom video in a full-screen, parallax background.",
    preview: () => <HeroPreview />,
  },
  {
    icon: TrendingUp,
    title: "Proven Track Record",
    description:
      "Animated career stats pulled from your Zillow profile.",
    preview: () => <TrackRecordPreview />,
  },
  {
    icon: Monitor,
    title: "Digital Footprint",
    description:
      "Portal views, saves, and per-platform engagement breakdowns.",
    preview: () => <DigitalFootprintPreview />,
  },
  {
    icon: BarChart3,
    title: "Market Performance",
    description:
      "Days-on-market comparison against local averages with a demand gauge.",
    preview: () => <MarketComparisonPreview />,
  },
  {
    icon: Star,
    title: "Client Reviews",
    description:
      "5-star Zillow reviews with an AI-generated summary.",
    preview: () => <ReviewsPreview />,
  },
  {
    icon: Calendar,
    title: "Showing Activity",
    description:
      "Calendar heat map of showings and open houses.",
    preview: () => <ShowingsPreview />,
  },
  {
    icon: Home,
    title: "Listings Gallery",
    description:
      "Swipeable carousel with photos, price, and property details.",
    preview: () => <ListingsPreview />,
  },
  {
    icon: MapPin,
    title: "Sales Coverage Map",
    description:
      "Interactive map pinning every sold property to show your geographic reach.",
    preview: () => <MapPreview />,
  },
  {
    icon: Users,
    title: "Meet the Team",
    description:
      "Agent headshots and names so prospects can see who they'll be working with.",
    preview: () => <TeamPreview />,
  },
];

/* ------------------------------------------------------------------ */
/*  Modal                                                              */
/* ------------------------------------------------------------------ */

export function SpotlightIntroModal({
  isOpen,
  onClose,
}: SpotlightIntroModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-5xl max-h-[85vh] overflow-y-auto border-slate-200 bg-white p-0"
        hideCloseButton
      >
        {/* Header */}
        <div className="px-6 pt-8 pb-2 sm:px-10 sm:pt-10">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full"
              style={{ background: "linear-gradient(135deg, hsl(190 95% 45%), hsl(270 85% 65%))" }}
            >
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <DialogTitle className="text-2xl sm:text-3xl font-bold text-slate-900">
              Introducing Your Spotlight
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Spotlight feature introduction
          </DialogDescription>
          <div className="text-base text-slate-500 space-y-2">
            <p>
              Your listings already tell a story. Spotlight turns that data into a
              single, polished page that sells{" "}
              <span className="text-slate-800 font-medium">you</span>.
            </p>
            <p>
              It pulls from your North reports, Zillow profile, showing
              activity, and team info to automatically build a shareable
              marketing page. Career stats, listing views, days-on-market
              comparisons, client reviews, interactive maps, and more. All
              kept up to date, no design skills needed.
            </p>
            <p>
              Share it with prospective sellers to show exactly why your team
              is the right choice.
            </p>
          </div>
        </div>

        {/* Example callout */}
        <div className="mx-6 sm:mx-10 mt-1 mb-1">
          <a
            href={EXAMPLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            <span>See a live example</span>
            <span
              className="font-mono font-medium"
              style={{ color: "hsl(190 95% 40%)" }}
            >
              {EXAMPLE_URL}
            </span>
            <ExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-400" />
          </a>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-6 py-4 sm:px-10">
          {features.map(({ icon: Icon, title, description, preview }) => (
            <div
              key={title}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start gap-3 mb-2">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                  style={{ background: "hsl(190 95% 45% / 0.1)" }}
                >
                  <Icon
                    className="h-4 w-4"
                    style={{ color: "hsl(190 95% 40%)" }}
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">
                    {title}
                  </p>
                  <p className="text-xs leading-relaxed text-slate-400">
                    {description}
                  </p>
                </div>
              </div>
              {preview && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  {preview()}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-slate-200 bg-white/80 px-6 py-4 backdrop-blur sm:flex-row sm:justify-end sm:px-10">
          <Button variant="outline" asChild>
            <a href={EXAMPLE_URL} target="_blank" rel="noopener noreferrer">
              See it Live
              <ExternalLink className="ml-2 h-3.5 w-3.5" />
            </a>
          </Button>
          <Button
            onClick={onClose}
            style={{ background: "hsl(190 95% 45%)" }}
            className="text-white hover:opacity-90 font-semibold"
          >
            Get Started
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
